import React from 'react';
import { Drawer, Form, Input, Select, Button, Dropdown, Avatar, message } from 'antd';
import { workwiseServices } from '../../../../Api/api';
import defaultProfileImage from '../../../../assets/defult-profile-logo.png';
import worldIcon from '../../../../assets/world.svg';
import lockIcon from '../../../../assets/lock.svg';
import './CreateMindMapDrawer.css';

// Small reusable async search Select for users
function PeopleSelect({
  value,
  onChange,
  placeholder,
  disabled,
}) {
  const [options, setOptions] = React.useState([]);
  const [fetching, setFetching] = React.useState(false);

  const fetchSuggestions = React.useMemo(
    () =>
      async (search) => {
        if (!search) {
          setOptions([]);
          return;
        }
        setFetching(true);
        try {
          const payload = {
            notRequired: [],
            pageNo: 1,
            pageSize: 20,
            referenceId: '00000000-0000-0000-0000-000000000000',
            removeSelf: true,
            search,
            types: [1],
          };
          const res = await workwiseServices.getAllSuggestion(payload);
          const data = res?.data?.data || [];
          setOptions(
            data.map((member) => ({
              value: member.id,
              label: member.name,
              meta: member,
            }))
          );
        } catch (e) {
          message.error('Failed to load suggestions');
        } finally {
          setFetching(false);
        }
      },
    []
  );

  // Debounce typing
  const debouncedSearch = React.useMemo(() => {
    let t;
    return (q) => {
      clearTimeout(t);
      t = setTimeout(() => fetchSuggestions(q), 300);
    };
  }, [fetchSuggestions]);

  return (
    <Select
      mode="multiple"
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      showSearch
      labelInValue={false}
      filterOption={false}
      onSearch={debouncedSearch}
      notFoundContent={fetching ? 'Searching…' : 'Type to search users'}
      disabled={disabled}
      optionLabelProp="label"
    >
      {options.map((opt) => (
        <Select.Option key={opt.value} value={opt.value} label={opt.label}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Avatar size="small" src={opt.meta?.image || defaultProfileImage} />
            <div>
              <div style={{ fontWeight: 500 }}>{opt.label}</div>
              {opt.meta?.designation && (
                <div style={{ fontSize: 12, color: '#6b7280' }}>{opt.meta.designation}</div>
              )}
            </div>
          </div>
        </Select.Option>
      ))}
    </Select>
  );
}

const PRIVACY = {
  PUBLIC: 'public',
  PRIVATE: 'private',
};

export default function CreateMindMapDrawer({
  open,
  onClose,
  onCreate,
  template,
}) {
  const [form] = Form.useForm();
  const [privacy, setPrivacy] = React.useState(PRIVACY.PUBLIC);
  const [collaborators, setCollaborators] = React.useState([]); // array of ids
  const [readers, setReaders] = React.useState([]); // array of ids

  React.useEffect(() => {
    if (!open) {
      form.resetFields();
      setCollaborators([]);
      setReaders([]);
      setPrivacy(PRIVACY.PUBLIC);
    }
    // When opened with a template, prefill fields
    if (open && template) {
      try {
        form.setFieldsValue({
          name: template.name || '',
          description: template.description || template.desc || '',
        });
      } catch (e) {
        // ignore
      }
    }
  }, [open, form]);

  const items = [
    {
      key: PRIVACY.PUBLIC,
      label: (
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <img src={worldIcon} alt="Public" style={{ width: 16, height: 16 }} /> Public
        </span>
      ),
    },
    {
      key: PRIVACY.PRIVATE,
      label: (
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <img src={lockIcon} alt="Private" style={{ width: 16, height: 16 }} /> Private
        </span>
      ),
    },
  ];

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const formData = new FormData();
      formData.append('name', values.name.trim());
      formData.append('description', values.description || '');
      formData.append('privacyId', privacy === PRIVACY.PUBLIC ? 1 : 2);
      formData.append('referenceId', '00000000-0000-0000-0000-000000000000');
      formData.append('referenceType', 1);
      formData.append('directoryId', '00000000-0000-0000-0000-000000000000');
      formData.append('documentType', 9);
      formData.append('attachments[0].name', values.name.trim());

      let memberIndex = 0;
      if (privacy === PRIVACY.PRIVATE) {
        readers.forEach(id => {
          formData.append(`members[${memberIndex}].memberId`, id);
          formData.append(`members[${memberIndex}].memberRightType`, 1);
          memberIndex++;
        });
      }
      collaborators.forEach(id => {
        formData.append(`members[${memberIndex}].memberId`, id);
        formData.append(`members[${memberIndex}].memberRightType`, 2);
        memberIndex++;
      });

      const response = await workwiseServices.addDocument(formData);
      message.success('Document created successfully');

      onCreate?.();

      // If opened via a template, open the new document with the templateId query
      if (template) {
        try {
          const document = response?.data?.data?.[0];
          const documentId = document?.attachments?.[0]?.attachmentId;
          const templateId = template?._id;
          if (documentId && templateId) {
            const url = `/${documentId}/?templateId=${templateId}`;
            window.open(url, '_blank');
            onClose();
            return;
          }
        } catch (templateError) {
          console.error('Failed to open document with template:', templateError);
          message.error('Document created, but failed to open with template.');
        }
      }

      onClose();
    } catch (error) {
      const errorMessage = error?.response?.data?.message || 'Failed to create document';
      message.error(errorMessage);
    }
  };

  const CreateButtonBar = (
    <div className="cpd-bar">
      <Dropdown
        menu={{
          items,
          onClick: (e) => {
            if (e.key === PRIVACY.PUBLIC) setPrivacy(PRIVACY.PUBLIC);
            if (e.key === PRIVACY.PRIVATE) setPrivacy(PRIVACY.PRIVATE);
          },
        }}
        placement="topLeft"
        trigger={["click"]}
      >
        <div className="cpd-privacy">
          <Button
            type="text"
            shape="circle"
          >
            {privacy === PRIVACY.PUBLIC ? <img src={worldIcon} alt="Public" style={{ width: 16, height: 16 }} /> : <img src={lockIcon} alt="Private" style={{ width: 16, height: 16 }} />}
          </Button>
        </div>
      </Dropdown>
      <Button
        type="primary"
        size="large"
        block
        className="cpd-create-btn"
        onClick={handleSubmit}
      >
        Create
      </Button>
    </div>
  );

  return (
    <Drawer
      className="cpd-drawer"
      title={<span style={{ fontWeight: 600, color: '#526bb1' }}>Create MindMap</span>}
      open={open}
      onClose={onClose}
      width={640}
      destroyOnClose={false}
      styles={{
        body: { background: '#f5f6f7', padding: 16 }
      }}
    >
      <div className="cpd" style={{
        background: '#fff',
        borderRadius: 8,
        padding: '20px'
      }}>
        <div>
          <Form form={form} layout="vertical" requiredMark={true}>
            <Form.Item
              label={<span className="cpd-label">Name</span>}
              name="name"
              rules={[{ required: true, message: 'Enter Name' }]}
            >
              <Input placeholder="Enter Name" />
            </Form.Item>

            <Form.Item
              label={<span className="cpd-label">Collaborators</span>}
            >
              <PeopleSelect
                value={collaborators}
                onChange={setCollaborators}
                placeholder="Select Collaborators"
              />
            </Form.Item>

            {privacy === PRIVACY.PRIVATE && (
              <Form.Item
                label={<span className="cpd-label">Readers</span>}
              >
                <PeopleSelect
                  value={readers}
                  onChange={setReaders}
                  placeholder="Select Member"
                />
              </Form.Item>
            )}

            <Form.Item
              label={<span className="cpd-label">Description</span>}
              name="description"
            >
              <Input.TextArea placeholder="Enter Description" showCount maxLength={500} rows={3} />
            </Form.Item>
          </Form>
        </div>

        <div style={{
          paddingTop: 8
        }}>
          {CreateButtonBar}
        </div>
      </div>
    </Drawer>
  );
}
